import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  DataTable,
  InputWithUnit,
  Select,
  ToggleSwitch,
  type DataTableColumn,
} from '@enamelai/design-system'
import { useTheme } from '@context/ThemeContext'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { createSuccessToast } from '@utils/notificationToast.util'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import {
  BACKUP_FREQUENCY_OPTIONS,
  BACKUP_NOW_ENABLED,
  BACKUP_STATUS_BADGES,
  BACKUP_TIME_OPTIONS,
  BACKUP_TOGGLES,
  BackupHistoryRow,
  BackupSettingsForm,
  DEFAULT_BACKUP_SETTINGS,
  RETENTION_UNIT_OPTIONS,
} from './backupSettings.constants'
import './BackupStyle.css'

const readHistorySortValue = (
  row: BackupHistoryRow,
  columnId: string,
): string => {
  if (columnId === 'date') {
    const time = Date.parse(row.date.replace(' - ', ' '))
    return Number.isNaN(time) ? row.date : String(time).padStart(15, '0')
  }
  if (columnId === 'size') {
    return String(parseFloat(row.size) || 0).padStart(12, '0')
  }
  return BACKUP_STATUS_BADGES[row.status].label.toLowerCase()
}

const Backup = (): React.ReactElement => {
  const { setHeaderButtons } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'

  const [savedSettings, setSavedSettings] = useState<BackupSettingsForm>(
    () => ({ ...DEFAULT_BACKUP_SETTINGS }),
  )
  const [settings, setSettings] = useState<BackupSettingsForm>(() => ({
    ...DEFAULT_BACKUP_SETTINGS,
  }))
  const [history] = useState<BackupHistoryRow[]>([])
  const [historySort, toggleHistorySort] = useTableSort()

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(savedSettings),
    [savedSettings, settings],
  )
  const fieldsDisabled = !settings.allowBackup

  const updateField = <K extends keyof BackupSettingsForm>(
    field: K,
    value: BackupSettingsForm[K],
  ): void => {
    setSettings((prev) => ({ ...prev, [field]: value }))
  }

  const handleDiscard = useCallback((): void => {
    setSettings({ ...savedSettings })
  }, [savedSettings])

  const handleSave = useCallback((): void => {
    setSavedSettings({ ...settings })
    showToast(
      createSuccessToast(
        'Backup settings saved',
        'Your backup configuration has been updated successfully.',
      ),
    )
  }, [settings, showToast])

  useEffect(() => {
    setHeaderButtons(
      isDirty || BACKUP_NOW_ENABLED ? (
        <>
          {isDirty ? (
            <>
              <Button
                className="settings-ds-button"
                variant="secondary"
                size="md"
                type="button"
                onClick={handleDiscard}
              >
                Cancel
              </Button>
              <Button
                className="settings-ds-button"
                variant="primary"
                size="md"
                type="button"
                onClick={handleSave}
              >
                Save changes
              </Button>
            </>
          ) : null}
          {BACKUP_NOW_ENABLED ? (
            <Button
              className="settings-ds-button"
              variant={isDirty ? 'secondary' : 'primary'}
              size="md"
              type="button"
              disabled={!settings.allowBackup}
            >
              Backup now
            </Button>
          ) : null}
        </>
      ) : null,
    )
  }, [
    handleDiscard,
    handleSave,
    isDirty,
    setHeaderButtons,
    settings.allowBackup,
  ])

  useEffect(() => () => setHeaderButtons(null), [setHeaderButtons])

  const historyColumns: DataTableColumn<BackupHistoryRow>[] = useMemo(
    () => [
      { id: 'date', label: 'Date', sortable: true },
      { id: 'size', label: 'Size', sortable: true },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        renderCell: (row) => {
          const badge = BACKUP_STATUS_BADGES[row.status]
          return (
            <Badge mode={badgeMode} color={badge.color} label={badge.label} />
          )
        },
      },
    ],
    [badgeMode],
  )

  const sortedHistory = useMemo(
    () => sortRows(history, historySort, readHistorySortValue),
    [history, historySort],
  )
  const historyPage = usePagedRows(sortedHistory)

  return (
    <div className="settings-backup">
      <section className="settings-backup-section">
        <label className="settings-backup-toggle settings-backup-toggle--title">
          <ToggleSwitch
            checked={settings.allowBackup}
            aria-label="Allow backup"
            onChange={(checked) => updateField('allowBackup', checked)}
          />
          <span className="settings-backup-section-title">Allow backup</span>
        </label>

        <div className="settings-backup-fields-grid">
          <Select
            label="Backup frequency"
            options={BACKUP_FREQUENCY_OPTIONS}
            value={settings.backupFrequency}
            disabled={fieldsDisabled}
            onChange={(value) =>
              updateField(
                'backupFrequency',
                value as BackupSettingsForm['backupFrequency'],
              )
            }
          />
          <Select
            label="Backup time"
            leadingIcon="clock"
            options={BACKUP_TIME_OPTIONS}
            value={settings.backupTime}
            disabled={fieldsDisabled}
            onChange={(value) => updateField('backupTime', String(value))}
          />
          <InputWithUnit
            label="Retention period"
            placeholder="30"
            value={settings.retentionPeriod}
            unit={settings.retentionUnit}
            units={RETENTION_UNIT_OPTIONS}
            disabled={fieldsDisabled}
            onChange={(value) =>
              updateField('retentionPeriod', value.replace(/\D/g, ''))
            }
            onUnitChange={(unit) =>
              updateField(
                'retentionUnit',
                unit as BackupSettingsForm['retentionUnit'],
              )
            }
          />
        </div>

        <div className="settings-backup-toggles-grid">
          {BACKUP_TOGGLES.map(({ key, label }) => (
            <label key={key} className="settings-backup-toggle">
              <ToggleSwitch
                checked={settings[key]}
                disabled={fieldsDisabled}
                aria-label={label}
                onChange={(checked) => updateField(key, checked)}
              />
              <span className="settings-backup-toggle-label">{label}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="settings-backup-section settings-backup-history">
        <h3 className="settings-backup-section-title settings-backup-history-title">
          Backup history
        </h3>
        <DataTable<BackupHistoryRow>
          className="settings-backup-table"
          columns={historyColumns}
          rows={historyPage.pageRows}
          state={history.length === 0 ? 'empty' : 'default'}
          emptyState={{
            title: 'No backups yet',
            description: 'Completed and failed backups will show up here.',
          }}
          sortColumnId={historySort.columnId}
          sortDirection={
            historySort.columnId ? historySort.direction : undefined
          }
          onSort={toggleHistorySort}
          pagination={historyPage.pagination}
        />
      </section>
    </div>
  )
}

export default Backup
